import styled from 'styled-components';
import { usePatternContext } from '../../context/PatternContext';
import { useEffect, useState } from 'react';
import Summary from './myAlgorithm/result/report/Summary';
import ResultDetail from './myAlgorithm/result/report/ResultDetail';
import { useNavigate, useParams } from 'react-router-dom';
import SelectedPattern from './myAlgorithm/result/SelectedPattern';
import Footer from '../components/footer/Footer';
import PageTop from '../components/pageTop/PageTop';
import QuotaService from '../../service/quota/Quota';
import AlgorithmService from '../../service/auction/AlgorithmService';
import { PageContainer } from '../components/common/PageComponent.style';
import { RegionProps, SidoList } from '../search/FilterData';
import RegionService from '../../service/Region';
import SaveModal from './myPatternList/SaveModal';
import Toast from '../components/toast/Toast';
import RecommendList from './myAlgorithm/result/RecommendList';
import { ListDataProps } from '../components/list/data';

const PatternResult = () => {
  const navigate = useNavigate();
  const { setPIdx, patternInfo, setPatternInfo } = usePatternContext();
  const [data, setData] = useState([]);
  const [analysisDetail, setAnalysisDetail] = useState<any[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const [sigunguList, setSigunguList] = useState<RegionProps[]>([]);
  const [emdList, setEmdList] = useState<RegionProps[]>([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [listData, setListData] = useState<ListDataProps[]>([]);

  const { number } = useParams();

  // 서비스 이용권 사용
  const serviceQuota = async (p_idx: number) => {
    try {
      const data = {
        type: 'I',
        algorithmIdx: p_idx,
      };
      await QuotaService.use(data);
    } catch (err) {
      console.log(err, '발생');
    }
  };

  const getReport = async (algorithmIdx: number) => {
    try {
      const res = await AlgorithmService.getAlgorithmAnalysis(algorithmIdx);
      setData(res.data.result?.analysis || []);
      setAnalysisDetail(res.data.result?.analysis_detail ?? []);
      setPatternInfo(res.data);
      setIsSaved(res.data.is_saved === 'Y');
      setPIdx(res.data.algorithm_idx);
      serviceQuota(algorithmIdx);
    } catch (error) {
      console.log(error);
    }
  };

  const getAlgorithmRecommend = async (algorithmIdx: number) => {
    try {
      const res = await AlgorithmService.getAlgorithmRecommend(
        algorithmIdx,
        'DA',
        5,
        1,
      );

      setListData(res.data.list);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (number) {
      getReport(Number(number));
      getAlgorithmRecommend(Number(number));
    }
  }, [number]);

  const getRegionLabelFromPnu = (
    pnu: string,
    sigunguList: RegionProps[],
    emdList: RegionProps[],
  ) => {
    const sido = SidoList.find((s) => s.code === pnu.substring(0, 2))?.region;
    const sigungu =
      sigunguList.find((s) => s.code === pnu.substring(0, 5))?.region ?? '';
    const emd =
      emdList.find((e) => e.code === pnu.substring(0, 8))?.region ?? '';

    return { sido, sigungu, emd };
  };

  useEffect(() => {
    const fetchRegionData = async () => {
      if (!patternInfo.pnu) return;

      const sidoCode = patternInfo.pnu.substring(0, 2);
      const sigunguCode = patternInfo.pnu.substring(0, 5);

      try {
        const sigunguRes = await RegionService.list(sidoCode);
        setSigunguList(sigunguRes.data);

        const emdRes = await RegionService.list(sigunguCode);
        setEmdList(emdRes.data);
      } catch (err) {
        console.error('지역 정보 불러오기 실패:', err);
      }
    };

    fetchRegionData();
  }, [patternInfo.pnu]);

  useEffect(() => {
    if (!showToast) return;
    navigate('/myAlgorithmList');
  }, [showToast]);

  return (
    <PageContainer>
      <PageTop text="알고리즘 분석결과" />
      <Content>
        <Section>
          <SelectedPattern sigunguList={sigunguList} emdList={emdList} />
          <ButtonBox>
            <EditButton
              onClick={() => {
                navigate('/myAlgorithm', {
                  state: {
                    edit: true,
                    region: getRegionLabelFromPnu(
                      patternInfo.pnu,
                      sigunguList,
                      emdList,
                    ),
                  },
                });
              }}
            >
              수정하기
            </EditButton>
            <SaveButton
              onClick={() => setShowSaveModal(true)}
              disabled={isSaved}
            >
              저장하기
            </SaveButton>
          </ButtonBox>
        </Section>
        {data.length > 0 && <Summary data={data} />}
        {data.length > 0 && (
          <ResultDetail analysisDetail={analysisDetail} analysis={data} />
        )}
        <RecommendList listData={listData} />
      </Content>
      {showSaveModal && (
        <SaveModal
          idx={Number(number)}
          setShowToast={setShowToast}
          onClose={() => setShowSaveModal(false)}
        />
      )}
      {showToast && (
        <Toast
          message={'알고리즘 저장 완료'}
          position={40}
          onClose={() => {
            setShowToast(null);
          }}
        />
      )}
      <Footer />
      <Bottom>
        <BottomSaveButton
          disabled={isSaved}
          onClick={() => setShowSaveModal(true)}
        >
          저장
        </BottomSaveButton>
        <BottomSearchButton
          onClick={() => {
            const stateToSend = {
              selectedItem: patternInfo,
            };

            navigate('/aiRecommendation', {
              state: stateToSend,
            });
          }}
        >
          추천물건 검색
        </BottomSearchButton>
      </Bottom>
    </PageContainer>
  );
};

export default PatternResult;

const Content = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: #f7f8fc;
`;

const Section = styled.div`
  display: flex;
  padding: 24px 16px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  background-color: #fff;
`;

const ButtonBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const Button = styled.button`
  outline: none;
  background-color: #fff;

  display: flex;
  height: 40px;
  padding: 9px 16px;
  justify-content: center;
  align-items: center;

  border-radius: 8px;
  border: 1px solid;
  background: #fff;

  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 145%;
  letter-spacing: -0.28px;
`;

const EditButton = styled(Button)`
  border-color: #e1e2e4;
  color: #616161;
  flex: 1 0 0;
`;

const SaveButton = styled(Button)`
  border-color: #008aff;
  color: #008aff;
  flex: 1 0 0;

  &:disabled {
    border-color: rgba(55, 56, 60, 0.16);
    color: rgba(55, 56, 60, 0.16);
  }
`;

const Bottom = styled.div`
  position: fixed;
  bottom: 0;

  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 8px;
  border-top: 1px solid #e5e8ea;
  background: #fff;
`;

const BottomSaveButton = styled(Button)`
  background: rgba(112, 115, 124, 0.08);
  color: #616161;
  font-size: 16px;
  border: none;
  padding: 12px 28px;

  &:disabled {
    background-color: #f7f7f8;
    color: rgba(55, 56, 60, 0.16);
  }
`;

const BottomSearchButton = styled(Button)`
  background: #008aff;
  color: #fff;
  flex: 1 0 0;
  font-size: 16px;
  border: none;
  padding: 12px 28px;
`;
